import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Document, DocumentType, getDocumentTypeLabel } from '../../api/documents';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface EditDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  document: Document | null;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  document,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    type: DocumentType.OTHER,
    fileUrl: '',
    description: '',
    tags: [] as string[],
    metadata: {
      issueDate: '',
      expiryDate: '',
      issuer: '',
      reference: '',
    },
    name: '',
    mimeType: '',
    fileSize: 0,
    birdId: '',
    healthTestId: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Populate form when document changes
  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title || '',
        type: document.type || DocumentType.OTHER,
        fileUrl: document.fileUrl || '',
        description: document.description || '',
        tags: document.tags || [],
        metadata: {
          issueDate: document.metadata?.issueDate || '',
          expiryDate: document.metadata?.expiryDate || '',
          issuer: document.metadata?.issuer || '',
          reference: document.metadata?.reference || '',
        },
        name: document.name || '',
        mimeType: document.mimeType || '',
        fileSize: document.fileSize || 0,
        birdId: document.birdId || '',
        healthTestId: document.healthTestId || '',
      });
    }
  }, [document]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        fileSize: formData.fileSize || undefined,
        birdId: formData.birdId || undefined,
        healthTestId: formData.healthTestId || undefined,
        metadata: Object.values(formData.metadata).some(v => v) ? formData.metadata : undefined,
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error updating document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  if (!document) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Edit Document" onClose={onClose} />
      <form id="edit-document-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-700">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Enter document title"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Type */}
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-slate-700">
                      Document Type *
                    </label>
                    <select
                      id="type"
                      required
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as DocumentType }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    >
                      {Object.values(DocumentType).map((type) => (
                        <option key={type} value={type}>
                          {getDocumentTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* File Size */}
                  <div>
                    <label htmlFor="fileSize" className="block text-sm font-medium text-slate-700">
                      File Size (bytes)
                    </label>
                    <input
                      type="number"
                      id="fileSize"
                      min="0"
                      value={formData.fileSize || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, fileSize: parseInt(e.target.value) || 0 }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* File URL */}
                <div>
                  <label htmlFor="fileUrl" className="block text-sm font-medium text-slate-700">
                    File URL *
                  </label>
                  <input
                    type="url"
                    id="fileUrl"
                    required
                    value={formData.fileUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, fileUrl: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="https://example.com/document.pdf"
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Document description..."
                  />
                </div>

                {/* Tags */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
                    Tags
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      className="flex-1 min-w-0 block w-full border-gray-300 rounded-none rounded-l-md focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Add tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-slate-500 text-sm"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-cyan-500 focus:outline-none focus:bg-cyan-500 focus:text-white"
                          >
                            <XMarkIcon className="h-2 w-2" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="edit-document-form"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Document'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default EditDocumentModal; 